import { useCallback, useEffect, useRef, useState } from 'react';

/** Explicit, short recordings only. Tracks are always released when the pane closes. */
export function useCommentVoice(onClip: (file: File, duration: number) => void, onError: (message: string) => void) {
  const [recording, setRecording] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const recorder = useRef<MediaRecorder | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const session = useRef<{ cancelled: boolean; finalizing: boolean } | null>(null);
  const generation = useRef(0);
  const alive = useRef(true);
  const clipRef = useRef(onClip); clipRef.current = onClip;
  const errorRef = useRef(onError); errorRef.current = onError;
  const finish = useCallback((cancel = false) => {
    generation.current += 1;
    const active = session.current;
    if (active) active.cancelled = cancel;
    const stopping = recorder.current?.state === 'recording';
    if (active && !cancel && (stopping || active.finalizing)) {
      active.finalizing = true;
      setFinalizing(true);
    } else {
      if (active) active.finalizing = false;
      setFinalizing(false);
    }
    if (stopping) recorder.current!.stop();
    stream.current?.getTracks().forEach(track => track.stop());
    stream.current = null;
    setRecording(false);
    setRequesting(false);
  }, []);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; generation.current += 1; if (session.current) session.current.cancelled = true; recorder.current?.state === 'recording' && recorder.current.stop(); stream.current?.getTracks().forEach(track => track.stop()); };
  }, []);
  useEffect(() => {
    if (!recording) return;
    const timer = window.setInterval(() => setSeconds(s => s + 1), 1000);
    const limit = window.setTimeout(() => finish(), 180000);
    return () => { window.clearInterval(timer); window.clearTimeout(limit); };
  }, [recording, finish]);
  const start = useCallback(async () => {
    if (recorder.current?.state === 'recording' || session.current?.finalizing) return;
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      errorRef.current('Voice recording is unavailable in this browser. You can attach an audio file instead.'); return;
    }
    const attempt = ++generation.current;
    setRequesting(true);
    try {
      const media = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (attempt !== generation.current) { media.getTracks().forEach(track => track.stop()); return; }
      stream.current = media;
      const mime = ['audio/webm;codecs=opus', 'audio/mp4', 'audio/ogg;codecs=opus'].find(type => MediaRecorder.isTypeSupported(type));
      const next = new MediaRecorder(media, mime ? { mimeType: mime } : undefined);
      const chunks: BlobPart[] = [];
      const began = Date.now();
      const recordingSession = { cancelled: false, finalizing: false };
      session.current = recordingSession;
      next.ondataavailable = event => { if (event.data.size) chunks.push(event.data); };
      next.onstop = () => {
        media.getTracks().forEach(track => track.stop());
        if (!alive.current) return;
        // Delivery precedes clearing busy state: Send/Close cannot race the final blob.
        if (!recordingSession.cancelled && chunks.length) {
          const type = next.mimeType || mime || 'audio/webm';
          const extension = type.includes('mp4') ? 'm4a' : type.includes('ogg') ? 'ogg' : 'webm';
          clipRef.current(new File(chunks, `Voice message ${new Date(began).toISOString().replace(/[:.]/g, '-')}.${extension}`, { type }), Date.now() - began);
        }
        if (session.current === recordingSession) {
          recordingSession.finalizing = false;
          recorder.current = null;
          setRecording(false); setFinalizing(false); setRequesting(false);
        }
      };
      next.onerror = () => {
        if (session.current !== recordingSession || !alive.current) return;
        finish(true); errorRef.current('Recording stopped unexpectedly. Please try again.');
      };
      recorder.current = next;
      next.start(250);
      setSeconds(0); setRecording(true);
    } catch {
      stream.current?.getTracks().forEach(track => track.stop()); stream.current = null;
      if (attempt === generation.current) errorRef.current('Microphone access was not available. Allow the microphone in your browser, or attach an audio file.');
    } finally { if (attempt === generation.current) setRequesting(false); }
  }, [finish]);
  return { recording, requesting, finalizing, seconds, start, stop: () => finish(false), cancel: () => finish(true) };
}
